import { useRef } from 'react'

import { getChatroomMessage } from '../../../../api'
import { EMPTY_FUNCTION } from '../../../../constants/common'
import { useUserData } from '../../../../hooks'
import { getChatroomMessageDeletedUpdate } from '../../ChatroomMessage'

import { useChatroomMessagesList } from './useChatroomMessagesList'
import { useChatroomMessagesRealtime } from './useChatroomMessagesRealtime'
import { useChatroomUnreadAnchor } from './useChatroomUnreadAnchor'

export function useChatroomMessages({
  chatroom,
  outboundUuid,
  uuid,
  onMarkAsRead,
  onMessageDeletedRemotely,
  onMessagesAdded,
  onMessageUpdatedRemotely,
  onReadBottomReached,
}) {
  const currentUser = useUserData()

  const updateRequestIdsRef = useRef({})

  const {
    handleAppendMessage,
    handleAppendMessages,
    handleLoadOlder,
    handleRefetchVisibleMessages,
    handleUpdateMessage,
    hasMoreOlder,
    isLoadingInitial,
    isLoadingOlder,
    latestMessageUuidRef,
    messages,
    messagesRef,
  } = useChatroomMessagesList({ outboundUuid, uuid })

  const {
    handleMessagesReachedBottom,
    handleMoveAnchorToMessage,
    isUnreadAnchorResolved,
    unreadAnchorMessageUuid,
  } = useChatroomUnreadAnchor({
    chatroom,
    hasMoreOlder,
    isLoadingInitial,
    isLoadingOlder,
    messages,
    uuid,
    onLoadOlderPage: handleLoadOlder,
  })

  function handleRealtimeAppend(freshMessages) {
    handleAppendMessages(freshMessages)
    handleMoveAnchorToMessage(freshMessages[0]?.uuid)
    onMessagesAdded?.(freshMessages)
  }

  function bumpUpdateRequestId(messageUuid) {
    const requestId = (updateRequestIdsRef.current[messageUuid] || 0) + 1

    updateRequestIdsRef.current[messageUuid] = requestId

    return requestId
  }

  function handleReplaceMessage(message) {
    bumpUpdateRequestId(message.uuid)
    handleUpdateMessage(message.uuid, message)
  }

  function handleRealtimeDelete(deletedUuid) {
    bumpUpdateRequestId(deletedUuid)

    const target = messagesRef.current.find(
      message => message.uuid === deletedUuid
    )

    handleUpdateMessage(
      deletedUuid,
      getChatroomMessageDeletedUpdate({
        currentUserId: currentUser?.id,
        message: target,
      })
    )
    onMessageDeletedRemotely?.(deletedUuid)
  }

  // Best-effort sync: only the latest response per message is applied.
  function handleRealtimeUpdate(updatedUuid) {
    if (messagesRef.current.some(message => message.uuid === updatedUuid)) {
      const requestId = bumpUpdateRequestId(updatedUuid)

      getChatroomMessage({ outboundUuid, uuid: updatedUuid })
        .then(response => {
          if (
            response &&
            updateRequestIdsRef.current[updatedUuid] === requestId
          ) {
            handleUpdateMessage(updatedUuid, response.data)
            onMessageUpdatedRemotely?.(response.data)
          }
        })
        .catch(EMPTY_FUNCTION)
    }
  }

  useChatroomMessagesRealtime({
    chatroom,
    latestMessageUuidRef,
    messagesRef,
    outboundUuid,
    uuid,
    onMessageDeleted: handleRealtimeDelete,
    onMessagesAppended: handleRealtimeAppend,
    onMessageUpdated: handleRealtimeUpdate,
  })

  // One-shot composition: mark as read on the backend FIRST so counters
  // already reflect the read state when the parent refetches them; unlock
  // dynamic divider movement in parallel.
  function handleReadBottomReached() {
    handleMessagesReachedBottom()
    onMarkAsRead?.().then(() => {
      onReadBottomReached?.()
    })
  }

  return {
    actions: {
      append: handleAppendMessage,
      loadOlder: handleLoadOlder,
      readBottomReached: handleReadBottomReached,
      refetchVisible: handleRefetchVisibleMessages,
      replace: handleReplaceMessage,
      update: handleUpdateMessage,
    },
    hasMoreOlder,
    isInitialReady: !isLoadingInitial && isUnreadAnchorResolved,
    isLoadingOlder,
    messages,
    unreadAnchorMessageUuid,
  }
}
